// src/components/layout/Sidebar.tsx
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import Icon from '../icons/Icons';
import { ChevronDown, X } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { RESET_STORE } from '../../store';
import { useAppDispatch } from '../../hooks/useAppDispatch';

interface SidebarProps {
  onNavigate?: () => void; // Callback for mobile navigation
}

const SidebarContainer = styled.div`
  background-color: ${({ theme }) => theme.colours.background.dark};
  color: ${({ theme }) => theme.colours.text.inverse};
  height: 100vh;
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  margin: 0.5rem;
  border-radius: 0.5rem;

  @media (max-width: 768px) {
    margin: 0;
    border-radius: 0;
    position: relative;
  }
`;

const MobileCloseButton = styled.button`
  display: none;
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colours.text.inverse};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.25rem;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    padding-right: 3rem;
  }
`;

const NavMenu = styled.nav`
  display: flex;
  flex-direction: column;
  flex: 1;
  font-size: 14px;
  overflow-y: auto;

  @media (max-width: 768px) {
    margin-top: 1rem;
  }
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: ${({ theme }) => theme.colours.text.inverse};
  text-decoration: none;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  transition: background-color 0.2s;
  border-left: 3px solid transparent;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    text-decoration: none;
  }

  &.active {
    background-color: rgba(255, 255, 255, 0.18);
    border-left: 3px solid rgba(255, 255, 255, 0.6);
    padding-left: calc(1rem - 3px);
    font-weight: 600;
  }

  svg {
    margin-right: 1rem;
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 1rem;

    &.active {
      padding-left: calc(1rem - 3px);
    }
  }
`;

const DropdownSection = styled.div`
  margin-bottom: 0.5rem;
`;

const DropdownHeader = styled.div<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  padding-left: ${({ isActive }) => isActive ? 'calc(1rem - 3px)' : '1rem'};
  color: ${({ theme }) => theme.colours.text.inverse};
  cursor: pointer;
  border-radius: 0.5rem;
  transition: background-color 0.2s;
  border-left: ${({ isActive }) => isActive ? '3px solid rgba(255, 255, 255, 0.6)' : '3px solid transparent'};
  background-color: ${({ isActive }) => isActive ? 'rgba(255, 255, 255, 0.18)' : 'transparent'};
  font-weight: ${({ isActive }) => isActive ? '600' : 'normal'};

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  svg {
    margin-right: 1rem;
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    padding-left: ${({ isActive }) => isActive ? 'calc(1rem - 3px)' : '1rem'};
    font-size: 1rem;
  }
`;

const DropdownContent = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '300px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-left: 1rem;

  @media (max-width: 768px) {
    margin-left: 1.5rem;
  }
`;

const ComingSoonBadge = styled.span`
  margin-left: auto;
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
  background-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  flex-shrink: 0;
`;

const DropdownItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  color: ${({ theme }) => theme.colours.text.inverse};
  text-decoration: none;
  border-radius: 0.5rem;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
  opacity: 0.8;
  transition: all 0.2s;
  border-left: 3px solid transparent;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    text-decoration: none;
    opacity: 1;
  }

  &.active {
    background-color: rgba(255, 255, 255, 0.18);
    border-left: 3px solid rgba(255, 255, 255, 0.6);
    padding-left: calc(1rem - 3px);
    opacity: 1;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 1rem;

    &.active {
      padding-left: calc(1rem - 3px);
    }
  }
`;

const ChevronIcon = styled.div<{ isOpen: boolean }>`
  margin-left: auto;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colours.background.dark};
  border-radius: 0.5rem;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  display: ${props => props.isOpen ? 'block' : 'none'};
  box-shadow: 0 -4px 6px rgba(0, 0, 0, 0.1);
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colours.text.inverse};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(231, 76, 60, 0.15);
    color: #FF6B6B;
  }

  &:active {
    background-color: rgba(231, 76, 60, 0.25);
  }
`;

const UserSection = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const UserAvatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const UserName = styled.span`
  font-weight: 600;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const UserRole = styled.span`
  font-size: 0.75rem;
  opacity: 0.7;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const Sidebar: React.FC<SidebarProps> = ({ onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isComplianceActive =
    location.pathname === '/compliance' || location.pathname.startsWith('/compliance-');
  const isUserMgmtActive = ['/user-roles', '/user-management', '/departments'].some(p =>
    location.pathname.startsWith(p)
  );

  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(isUserMgmtActive);
  const [isComplianceOpen, setIsComplianceOpen] = useState(isComplianceActive);

  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const userSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (userSectionRef.current && !userSectionRef.current.contains(e.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };
    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isUserDropdownOpen]);

  const handleLogout = async () => {
    await dispatch(logout());
    dispatch({ type: RESET_STORE });
    navigate('/login');
    onNavigate?.();
  };

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(prev => !prev);
  };

  const handleComplianceHeaderClick = () => {
    navigate('/compliance');
    setIsComplianceOpen(prev => !prev);
  };

  const handleNavClick = () => {
    onNavigate?.();
  };

  return (
    <SidebarContainer>
      <MobileCloseButton onClick={onNavigate}>
        <X size={24} />
      </MobileCloseButton>

      <Logo>Oilwell Asset</Logo>
      <NavMenu>
        <NavItem to="/dashboard" onClick={handleNavClick}>
          <Icon name='dashboard' />
          Dashboard
        </NavItem>

        <NavItem to="/clients" onClick={handleNavClick}>
          <Icon name='clientlist' />
          Customer List
        </NavItem>

        <NavItem to="/archive" onClick={handleNavClick}>
          <Icon name='archive' />
          Archive
        </NavItem>

        <NavItem to="/procurement" onClick={handleNavClick}>
          <Icon name='procurement' />
          Procurement
        </NavItem>

        <NavItem to="/maintenance" onClick={handleNavClick}>
          <Icon name='maintenance' />
          Maintenance
        </NavItem>

        <NavItem to="/inventory" onClick={handleNavClick}>
          <Icon name='inventory' />
          Inventory
        </NavItem>

        <DropdownSection>
          <DropdownHeader isActive={isComplianceActive} onClick={handleComplianceHeaderClick}>
            <Icon name='compliance' />
            Compliance
            <ChevronIcon isOpen={isComplianceOpen}>
              <ChevronDown size={16} />
            </ChevronIcon>
          </DropdownHeader>
          <DropdownContent isOpen={isComplianceOpen}>
            <DropdownItem to="/compliance-checklist" onClick={handleNavClick}>
              Checklist
            </DropdownItem>
            <DropdownItem to="/compliance-maintenance" onClick={handleNavClick}>
              Maintenance
            </DropdownItem>
          </DropdownContent>
        </DropdownSection>

        <NavItem to="/disposal" onClick={handleNavClick}>
          <Icon name='disposal' />
          Disposal
          <ComingSoonBadge>Soon</ComingSoonBadge>
        </NavItem>

        <NavItem to="/reports" onClick={handleNavClick}>
          <Icon name='reports' />
          Reports & Analytics
          <ComingSoonBadge>Soon</ComingSoonBadge>
        </NavItem>

        <DropdownSection>
          <DropdownHeader isActive={isUserMgmtActive} onClick={() => setIsUserManagementOpen(prev => !prev)}>
            <Icon name='userCircle' />
            User Management
            <ChevronIcon isOpen={isUserManagementOpen}>
              <ChevronDown size={16} />
            </ChevronIcon>
          </DropdownHeader>
          <DropdownContent isOpen={isUserManagementOpen}>
            <DropdownItem to="/user-roles" onClick={handleNavClick}>
              User Roles
            </DropdownItem>
            <DropdownItem to="/user-management" onClick={handleNavClick}>
              Users
            </DropdownItem>
            <DropdownItem to="/departments" onClick={handleNavClick}>
              Departments
            </DropdownItem>
          </DropdownContent>
        </DropdownSection>

        <NavItem to="/settings" onClick={handleNavClick}>
          <Icon name='gear' />
          Settings
        </NavItem>
        <NavItem to="/notifications" onClick={handleNavClick}>
          <Icon name='notification' />
          Notifications
        </NavItem>
      </NavMenu>

      <UserSection ref={userSectionRef} onClick={toggleUserDropdown}>
        <UserAvatar>
          <Icon name='userCircle' />
        </UserAvatar>
        <UserInfo>
          <UserName>{user?.name || 'User'}</UserName>
          <UserRole>
            {user?.role?.name || 'User'} <ChevronDown size={20} />
          </UserRole>
        </UserInfo>
        <UserDropdown isOpen={isUserDropdownOpen}>
          <DropdownButton data-testid="logout" onClick={handleLogout}>
            Logout
          </DropdownButton>
        </UserDropdown>
      </UserSection>
    </SidebarContainer>
  );
};

export default Sidebar;
